"use client";

import React from "react";
import { Activity, Apple, ArrowRight, Brain, Dna, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
interface TopicItem {
  id: string;
  title: string;
  description: string;
  to: string;
  count: string;
  icon: "health" | "nutrition" | "behavior" | "breeds" | "products";
}
const topicsData: TopicItem[] = [{
  id: "health",
  title: "Pet Health",
  description: "Preventive care, clinical symptom guides, and diagnostic advice vetted by licensed veterinarians.",
  to: "/PetHealthPage",
  count: "140+ Guides",
  icon: "health"
}, {
  id: "nutrition",
  title: "Nutrition",
  description: "Formulation breakdowns, life-stage feeding protocols, and evidence-informed ingredient analyses.",
  to: "/NutritionPage",
  count: "95+ Articles",
  icon: "nutrition"
}, {
  id: "behavior",
  title: "Behavior & Training",
  description: "Humane conditioning techniques, cognitive development, and habit correction for dogs and cats.",
  to: "/BehaviorTrainingPage",
  count: "80+ Protocols",
  icon: "behavior"
}, {
  id: "breeds",
  title: "Breeds",
  description: "Genetic predispositions, temperament profiles, physical traits, and exercise requirements.",
  to: "/BreedsPage",
  count: "120+ Profiles",
  icon: "breeds"
}, {
  id: "products",
  title: "Products & Reviews",
  description: "Laboratory-evaluated pet gear, ergonomic accessories, and objective safety assessments.",
  to: "/ProductsReviewsPage",
  count: "60+ Reviews",
  icon: "products"
}];
function TopicIcon({
  type
}: {
  type: TopicItem["icon"];
}) {
  const iconProps = {
    className: "size-6 text-foreground",
    strokeWidth: 1.75
  };
  switch (type) {
    case "health":
      return <Activity {...iconProps} />;
    case "nutrition":
      return <Apple {...iconProps} />;
    case "behavior":
      return <Brain {...iconProps} />;
    case "breeds":
      return <Dna {...iconProps} />;
    case "products":
      return <ShieldCheck {...iconProps} />;
    default:
      return <Activity {...iconProps} />;
  }
}
export default function Topics() {
  const prefersReduced = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReduced ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="explore-by-topic" data-nav="light" className="bg-background border-b border-border py-16 md:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-foreground font-sans text-xs font-semibold tracking-wider uppercase mb-4">
            <span className="size-1.5 rounded-full bg-foreground" />
            Core Hubs
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight leading-tight">
            Explore by topic
          </h2>
          <p className="mt-4 font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Navigate veterinary-reviewed guides, dietary standards, and behavioral research curated for cats and dogs.
          </p>
        </div>

        {/* 5-Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topicsData.map((topic, i) => {
          const isLastOnDesktop = i === 4;
          return <motion.div key={topic.id} data-index={i} variants={cardVariants} className={isLastOnDesktop ? "md:col-span-2 lg:col-span-1" : ""}>
                <Card className="h-full bg-card border-border rounded-xl shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-border/80 hover:shadow-md group flex flex-col justify-between p-6 sm:p-7">
                  <CardContent className="p-0 flex flex-col h-full justify-between">
                    <div>
                      {/* Card Top Row: Icon and Badge */}
                      <div className="flex items-center justify-between gap-4 mb-6">
                        <div className="size-12 rounded-lg bg-background border border-border flex items-center justify-center shadow-2xs group-hover:border-foreground/20 transition-colors">
                          <TopicIcon type={topic.icon} />
                        </div>
                        <span className="font-sans text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md border border-border">
                          {topic.count}
                        </span>
                      </div>

                      {/* Title & Description */}
                      <h3 className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground mb-3 leading-snug">
                        {topic.title}
                      </h3>
                      <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {topic.description}
                      </p>
                    </div>

                    {/* Action Link */}
                    <div className="pt-6 mt-6 border-t border-border/60">
                      <Link to={topic.to} className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors">
                        <span>Explore {topic.title}</span>
                        <ArrowRight className="size-4 text-foreground transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2} />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}